/**
 * pipeline.ts — the boundary ladder and the six governed stages.
 *
 * This is the spine of the product. Each stage transforms one artifact into the
 * next, and each transition is a boundary that must NOT be silently crossed:
 *
 *   raw response ≠ interpreted meaning ≠ reviewed asset ≠ approved version
 *   ≠ runtime projection ≠ permission to act
 *
 * The UI renders the ladder as a sticky ribbon and the stages as a vertical
 * governance chain (see egae-component-library §03).
 */

/** The boundary ladder shown in the top ribbon — the chain no stage may collapse. */
export const BOUNDARY_LADDER: readonly string[] = [
  "raw response",
  "interpreted meaning",
  "reviewed asset",
  "approved version",
  "runtime projection",
  "permission to act",
];

export interface PipelineStage {
  readonly n: number;
  readonly key: string;
  /** Route slug under #/. */
  readonly route: string;
  readonly step: string;
  readonly name: string;
  readonly entity: string;
  readonly text: string;
  /** The boundary this stage must not cross silently. */
  readonly boundary: string;
}

export const PIPELINE: readonly PipelineStage[] = [
  {
    n: 1,
    key: "capture",
    route: "evidence",
    step: "stage 01 · capture",
    name: "Evidence Item",
    entity: "EvidenceItem · evidenceId",
    text: "A raw response is normalized and stamped with provenance, source authority, method family, consent, and an explicit boundary on what it does not establish.",
    boundary: "raw response ≠ interpreted meaning",
  },
  {
    n: 2,
    key: "synthesize",
    route: "interpret",
    step: "stage 02 · synthesize",
    name: "Candidate Interpretation",
    entity: "CandidateInterpretation · interpretationId",
    text: "The AI proposes meaning that references its evidence and keeps its counter-evidence attached. It is offered as a candidate, never a fact, and is marked reviewRequired.",
    boundary: "interpreted meaning ≠ reviewed asset",
  },
  {
    n: 3,
    key: "review",
    route: "interpret",
    step: "stage 03 · review",
    name: "Review Decision",
    entity: "ReviewDecision · confirm/correct/reject/defer/split/merge",
    text: "A human exercises ownership: confirm, correct, reject, defer, split, or merge. Only an explicit review state can promote an interpretation toward an asset field.",
    boundary: "reviewed asset ≠ approved version",
  },
  {
    n: 4,
    key: "author",
    route: "author",
    step: "stage 04 · author",
    name: "Semantic Asset → Version",
    entity: "SemanticAsset · assetVersionId (immutable)",
    text: "Confirmed meaning becomes a stable, reviewable asset. On approval the version freezes — immutable, traceable to the evidence and decisions that produced it.",
    boundary: "approved version ≠ runtime projection",
  },
  {
    n: 5,
    key: "project",
    route: "project",
    step: "stage 05 · project",
    name: "Runtime Projection",
    entity: "RuntimeProjection · runtimeProjectionId",
    text: "Approved assets are compiled into a scoped, inspectable projection — no uncontrolled context dumps. Every projected instruction traces back to an asset version, policy scope, and evidence references.",
    boundary: "runtime projection ≠ permission to act",
  },
  {
    n: 6,
    key: "operate",
    route: "project",
    step: "stage 06 · operate",
    name: "Runtime Trace",
    entity: "RuntimeTrace · runtimeTraceId",
    text: "Behavior is logged so any output can be traced back through projection, retrieval, policy, asset, and evidence lineage — and corrected at the right layer.",
    boundary: "runtime behavior ≠ proof of validity",
  },
];

/** Eight evaluation dimensions the asset evaluator scores (alignment-asset-evaluator). */
export const EVALUATION_DIMENSIONS: readonly string[] = [
  "evidence quality",
  "construct fit",
  "wording quality",
  "conflict coverage",
  "retrieval fitness",
  "behavioral usefulness",
  "governance readiness",
  "projection eligibility",
];
